"use client"

import styled from "@emotion/styled"
import { GetFootersQuery } from "@eurodrive/apollo"
import { extractProperty, getNonUmbracoUrl, Link } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import {
  branchLocatorClicked,
  contactUsFooterClicked,
  phoneClick,
  whatsappClick,
} from "@eurodrive/utils/gtm"
import { Typography } from "@mui/material"
import Image from "next/image"
import { useLocale } from "next-intl"
import { useInView } from "react-intersection-observer"
import Container from "../../Container"
import { Desktop, Mobile } from "../../MediaQueries"
import { sharedColors } from "../../theme/colors"
import { TransitionUp } from "../../transitions/TransitionUp"
import { useInViewConfig } from "../../transitions/utils"
import MobileLinks from "./Accordion"

const FooterContainer = styled.div`
  padding: 48px 0;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 64px 0;
  }
  background-color: ${sharedColors.lightGrey};
`

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 80px;
  }
`

const CommonGrid = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
  }
  gap: 40px;
  width: 100%;
`

const LinksGrid = styled.div`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-column: 2 / -1;
  }
  gap: 40px;
  width: 100%;
`

const LogoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  width: fit-content;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const ContactColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  display: flex;
  gap: 8px;
  justify-content: center;
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledPhone = styled.div`
  display: flex;
  ${({ theme }) => theme.breakpoints.down("md")} {
    align-items: center;
  }
`

const LinksColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const LinksGroup = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledLinkItem = styled(Link)`
  text-decoration: none;
  color: ${sharedColors.black};
  text-decoration: none;
  padding: 8px 0;
  width: fit-content;
  :hover {
    box-shadow: inset 0 -1px 0 0 ${sharedColors.black};
  }
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const BottomBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    border-top: 1px solid ${sharedColors.black};
    gap: 16px;
  }
`

const StyledLegalLink = styled(Link)`
  text-decoration: none;
  :hover {
    box-shadow: inset 0 -1px 0 0 ${sharedColors.black};
  }
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const BottomLinksRow = styled.div`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 32px;
    flex-direction: row;
  }
`

const BottomLinks = styled.div`
  display: flex;
  gap: 12px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
  align-items: center;
`

const BottomLinksBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 24px;
  }
`

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
`

const MintBox = styled.div`
  padding: 16px 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

export default function Footer({
  footer_data,
}: {
  footer_data: GetFootersQuery
}) {
  const { ref: inViewRef, inView } = useInView(useInViewConfig)
  const locale = useLocale()
  const siteMapLabel = locale === "he" ? "מפת האתר" : "Site map"
  const footer = footer_data.footer
  const logo = extractProperty(footer, "logo")
  const phoneTitle = extractProperty(footer, "phoneTitle")
  const phone = extractProperty(footer, "phoneNumber")
  const phoneNumber = phone?.[0]?.url
  const phoneName = phone?.[0]?.name
  const whatsappIcon = extractProperty(footer, "whatsappIcon")
  const whatsappLink = extractProperty(footer, "whatsappLink")
  const whatsappUrl = whatsappLink?.[0]?.url
  const whatsappName = whatsappLink?.[0]?.name
  const socialItems = extractProperty(footer, "socialItems") || []
  const socialLinks = socialItems.map((el: any) => ({
    icon: extractProperty(el, "icon"),
    url: extractProperty(el, "link")?.[0]?.url,
    name: extractProperty(el, "link")?.[0]?.name,
    altText: extractProperty(el, "link")?.[0]?.altText,
  })) as {
    icon: string
    url: string
    name: string
    altText: string
  }[]
  const itemsList = extractProperty(footer, "itemsList") || []
  const links = itemsList.map((el: any) => {
    const title = extractProperty(el, "title")
    const catLinksArray = extractProperty(el, "categoriesLinks") || []
    const catLinks = catLinksArray.map((cat: any) => {
      return {
        name: cat.name,
        url: cat.url,
        type: cat.type,
      }
    })
    return {
      title,
      catLinks,
    }
  }) as {
    title: string
    catLinks: { name: string; url: string; type: string }[]
  }[]
  const legalContentItems = extractProperty(footer, "legalContentItems") || []
  const legalLinks = legalContentItems.map((el: any) => ({
    name: el.name,
    url: el.url,
    target: el.target,
  })) as { name: string; url: string; target: string }[]
  const rightsText = extractProperty(footer, "rightsText")

  const handlePhoneClick = () => {
    phoneClick(phoneNumber || "", "footer")
  }

  const handleWhatsAppClick = () => {
    whatsappClick(whatsappUrl || "", "footer")
  }

  const handleFooterLinkClick = (linkName: string, linkUrl: string) => {
    // Check if it's a branch/contact related link
    const branchKeywords = [
      "branch",
      "סניף",
      "location",
      "מיקום",
      "contact",
      "צור קשר",
    ]
    const isBranchLink = branchKeywords.some(
      (keyword) =>
        linkName.toLowerCase().includes(keyword) ||
        linkUrl.toLowerCase().includes(keyword),
    )

    if (isBranchLink) {
      branchLocatorClicked("footer_link", "footer")
    } else {
      contactUsFooterClicked(linkName, "footer")
    }
  }

  return (
    <FooterContainer ref={inViewRef} role="contentinfo">
      <Container paddingMobile>
        <TransitionUp $inView={inView}>
          <InnerContainer>
            <CommonGrid>
              <LogoColumn>
                <Image
                  src={getUmbracoMediaUrl(logo)}
                  alt={"euro drive עמוד הבית"}
                  width={145}
                  height={40}
                />
                <ContactColumn>
                  <StyledPhone>
                    <Desktop>
                      <Typography variant="body3" component={"h4"}>
                        {phoneTitle}
                      </Typography>
                    </Desktop>
                    <Mobile>
                      <Typography variant="body1" component={"h4"}>
                        {phoneTitle}
                      </Typography>
                    </Mobile>
                    {phoneNumber && (
                      <StyledLink href={phoneNumber} onClick={handlePhoneClick}>
                        <Desktop>
                          <Typography variant="body3">{phoneName}</Typography>
                        </Desktop>
                        <Mobile>
                          <Typography variant="body2">{phoneName}</Typography>
                        </Mobile>
                      </StyledLink>
                    )}
                  </StyledPhone>
                  {whatsappUrl && (
                    <StyledLink href={whatsappUrl} onClick={handleWhatsAppClick}>
                      <Image
                        src={getUmbracoMediaUrl(whatsappIcon)}
                        alt={""}
                        width={18}
                        height={18}
                      />
                      <Desktop>
                        <Typography variant="body3">{whatsappName}</Typography>
                      </Desktop>
                      <Mobile>
                        <Typography variant="body2" fontWeight={600}>
                          {whatsappName}
                        </Typography>
                      </Mobile>
                    </StyledLink>
                  )}
                </ContactColumn>
              </LogoColumn>
              <LinksGrid>
                {links.map((el, i) => (
                  <LinksColumn key={i}>
                    <Typography variant="body2" component={"h4"}>
                      {el.title}
                    </Typography>
                    <LinksGroup>
                      {el.catLinks.map((link, i) => (
                        <StyledLinkItem
                          key={i}
                          href={getNonUmbracoUrl(link.url)}
                          target={
                            link.type === "EXTERNAL" ? "_blank" : undefined
                          }
                          onClick={() =>
                            handleFooterLinkClick(link.name, link.url)
                          }
                        >
                          <Typography variant="body3">{link.name}</Typography>
                        </StyledLinkItem>
                      ))}
                    </LinksGroup>
                  </LinksColumn>
                ))}
              </LinksGrid>
              <MobileLinks megaMenuItems={links} />
            </CommonGrid>
            <BottomBox role="contentinfo">
              <BottomLinksRow>
                <SocialLinks>
                  {socialLinks.map((el, i) => (
                    <StyledLink
                      href={getNonUmbracoUrl(el.url || "")}
                      target="_blank"
                      key={i}
                      aria-label={el.altText || el.name}
                    >
                      <Image
                        src={getUmbracoMediaUrl(el.icon)}
                        alt={el.altText || el.name || ""}
                        width={24}
                        height={24}
                      />
                    </StyledLink>
                  ))}
                </SocialLinks>
                <BottomLinksBox>
                  <BottomLinks>
                    <StyledLegalLink href="/site-map">
                      <Desktop>
                        <Typography variant="body3">{siteMapLabel}</Typography>
                      </Desktop>
                      <Mobile>
                        <Typography variant="body2" fontWeight={600}>
                          {siteMapLabel}
                        </Typography>
                      </Mobile>
                    </StyledLegalLink>
                    {legalLinks.map((el, i) => (
                      <StyledLegalLink
                        href={getNonUmbracoUrl(el.url)}
                        target={el.target}
                        key={i}
                      >
                        <Desktop>
                          <Typography variant="body3">{el.name}</Typography>
                        </Desktop>
                        <Mobile>
                          <Typography variant="body2" fontWeight={600}>
                            {el.name}
                          </Typography>
                        </Mobile>
                      </StyledLegalLink>
                    ))}
                  </BottomLinks>
                  <Desktop fitContent>
                    <Typography variant="body3">{rightsText}</Typography>
                  </Desktop>
                  <Mobile fitContent>
                    <Typography variant="body2" fontWeight={600}>
                      {rightsText}
                    </Typography>
                  </Mobile>
                </BottomLinksBox>
              </BottomLinksRow>
              <MintBox>
                <Image
                  src={"/images/mint.png"}
                  alt={""}
                  width={129}
                  height={18}
                />
              </MintBox>
            </BottomBox>
          </InnerContainer>
        </TransitionUp>
      </Container>
    </FooterContainer>
  )
}
